import type { PropsWithChildren } from 'react';
import { useCallback, useContext, useState } from 'react';

import { DateRangePickerStateContext } from 'react-aria-components/DateRangePicker';
import { Dialog, DialogTrigger } from 'react-aria-components/Dialog';

import type { DateRangeValue, DateValue } from '../../index';
import { Button } from '../button/button';
import { RangeCalendar } from '../calendars/range_calendar';
import { Popover, type PopoverProps } from '../popover/popover';
import { Separator } from '../separator/separator';
import { Stack } from '../stack/stack';

import styles from './range_calendar_popover.module.css';

export type RangeCalendarPopoverProps = PropsWithChildren<{
  value?: DateRangeValue | null;
  defaultValue?: DateRangeValue | null;
  onChange?: (value: DateRangeValue) => void;
  onSubmit?: (value: DateRangeValue) => void;
  onClear?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  placement?: PopoverProps['placement'];
  offset?: number;
  canClear?: boolean;
  presets?: 'past' | 'future';
}>;

export function RangeCalendarPopover({
  children,
  value,
  defaultValue,
  onChange,
  onSubmit,
  onClear,
  onOpenChange: onOpenChangeProp,
  placement = 'top',
  offset = 8,
  canClear = false,
  presets = 'past',
}: RangeCalendarPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const pickerState = useContext(DateRangePickerStateContext);
  const [committedValueState, setCommittedValueState] =
    useState<DateRangeValue | null>(defaultValue ?? null);

  const committedValue =
    value !== undefined ? (value ?? null) : committedValueState;

  const [draftValue, setDraftValue] = useState<DateRangeValue | null>(
    committedValue,
  );
  const [draftFocusedValue, setDraftFocusedValue] = useState<DateValue | null>(
    committedValue?.start ?? committedValue?.end ?? null,
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setDraftValue(committedValue);
        setDraftFocusedValue(
          committedValue?.start ?? committedValue?.end ?? null,
        );
      } else {
        pickerState?.setOpen(false);
      }
      setIsOpen(open);
      onOpenChangeProp?.(open);
    },
    [committedValue, onOpenChangeProp, pickerState],
  );

  const handleClear = useCallback(() => {
    setDraftValue(null);
    setDraftFocusedValue(null);
    pickerState?.setOpen(false);
    setIsOpen(false);
    onOpenChangeProp?.(false);
    onClear?.();
  }, [onClear, onOpenChangeProp, pickerState]);

  const handleApply = useCallback(() => {
    if (draftValue) {
      if (value === undefined) {
        setCommittedValueState(draftValue);
      }
      onChange?.(draftValue);
      onSubmit?.(draftValue);
    }
    pickerState?.setOpen(false);
    setIsOpen(false);
    onOpenChangeProp?.(false);
  }, [draftValue, onChange, onSubmit, onOpenChangeProp, pickerState, value]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      {children}
      <Popover placement={placement} offset={offset}>
        <Dialog className={styles.dialog}>
          <RangeCalendar
            value={draftValue}
            presets={presets}
            onChange={(nextValue: DateRangeValue) => {
              setDraftValue(nextValue);
              setDraftFocusedValue(nextValue.start);
            }}
            focusedValue={draftFocusedValue ?? undefined}
            onFocusChange={(nextFocusedValue) => {
              setDraftFocusedValue(nextFocusedValue);
            }}
          />
          <Separator className={styles.divider} variant="sub" />
          <Stack direction="row" gap="m">
            {canClear && (
              <Button
                size="m"
                onPress={handleClear}
                variant="tertiary"
                isDisabled={!committedValue && !draftFocusedValue}
              >
                {committedValue ? 'Clear' : 'Close'}
              </Button>
            )}
            <Button size="m" onPress={handleApply}>
              Apply
            </Button>
          </Stack>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
